// Plain LaDesk (LiveAgent) chat integration.
//
// LaDesk's script is loaded once per page and a button object is created from it.
// Opening the chat is buttonInstance.onClick(), which is LaDesk's documented way
// to drive the chat from a trigger of your own. That replaces the old wrapper,
// which rendered a real LaDesk button into a hidden container and then faked
// clicks on it after monkey-patching its callbacks.
//
// createVirtualButton() is preferred because it renders no markup at all, so the
// site keeps its own card and nothing of LaDesk's ever reaches the page. It ships
// in track.js but is absent from LaDesk's public docs, so the documented
// createButton() stays as a fallback.
//
// WHEN THE SCRIPT LOADS. By default nothing is fetched until someone actually
// asks for the chat: the first open() pulls track.js in. Desktop can afford that
// because LaDesk answers with an in-page iframe, so opening the chat from the
// script's onload callback works.
//
// Touch devices are the exception. There LaDesk may answer with a chat it shows
// via window.open(), and Safari only allows that while the tap is still being
// handled — a chat opened later, from onload, is silently blocked. So on touch the
// script is pre-warmed instead, after the page has loaded and during idle time, so
// it is ready before the first tap without competing with the critical path.
//
// That exception is a workaround for a LaDesk setting, not a fact of life: turning
// on "Do not open in a pop-up on mobile devices" for the chat button makes mobile
// use the in-page iframe too, at which point the pre-warm can be deleted and every
// platform loads purely on click.
//
// A chat left open is the third reason to load without a click — see below.

// track.js reads its own server URL off this element's src, so the id is required.
const SCRIPT_ID = 'la_x2s6df8d';
const SCRIPT_SRC = 'https://simply.ladesk.com/scripts/track.js';

// LaDesk keeps an open conversation in a session cookie of its own and resumes it
// unprompted a moment after track.js runs, then empties the cookie when the
// visitor closes the chat. So surviving a page refresh only takes noticing the
// cookie and loading the script — the restore itself is entirely LaDesk's.
//
// Closing empties the value instead of dropping the cookie, so a bare
// "LaRunningChat_x=" means no chat and the match has to require a value.
const RUNNING_CHAT_COOKIE = /(?:^|;\s*)LaRunningChat_[^=;]*=[^;\s]/;

function hasRunningChat () {
	return RUNNING_CHAT_COOKIE.test(document.cookie);
}

// Readiness backstop, used only while a click is waiting to be replayed.
// onOnline/onOffline are the signals LaDesk documents, but which of them fires
// depends on how the button is configured in the LaDesk admin, so a click must
// never be left waiting on them forever.
const READY_POLL_MS = 250;
const READY_POLL_TRIES = 40;

/**
 * Returns a handle for opening the chat window. LaDesk itself is fetched on the
 * first open(), or pre-warmed after load on devices that may need a popup.
 *
 * @param {string} chatId LaDesk chat button id, taken from the LaDesk admin.
 * @returns {{open: function}}
 */
export function createLadeskChat (chatId) {
	var button = null;
	var ready = false;
	var unavailable = false;
	var loading = false;
	var openWhenReady = false;
	var pollsLeft = 0;

	// LaDesk only has a chat window to show once it has answered for the button.
	// isInitialized() reads the flag set by that answer, and is a second opinion
	// next to the onOnline/onOffline hooks.
	function isReady () {
		return button !== null && (ready || button.isInitialized());
	}

	function warn (reason) {
		if (window.console) {
			// The old code swallowed this in an empty catch. Say it out loud.
			console.warn('LaDesk chat could not be opened: ' + reason);
		}
	}

	function open () {
		load();

		if (isReady()) {
			// Synchronous inside the click handler, so Safari counts any
			// window.open() LaDesk does from here as gesture-initiated.
			button.onClick();
			return;
		}
		if (unavailable) {
			warn('script failed to load.');
			return;
		}
		// LaDesk has not answered yet — open the chat as soon as it does. The
		// budget is per click, so a later attempt is not starved by an earlier one.
		openWhenReady = true;
		pollsLeft = READY_POLL_TRIES;
		poll();
	}

	function flush () {
		ready = true;
		if (!openWhenReady) {
			return;
		}
		openWhenReady = false;
		button.onClick();
	}

	function poll () {
		if (!openWhenReady) {
			return;
		}
		if (isReady()) {
			flush();
			return;
		}
		if (pollsLeft <= 0) {
			openWhenReady = false;
			warn('the widget never finished initialising.');
			return;
		}
		pollsLeft--;
		setTimeout(poll, READY_POLL_MS);
	}

	function useButton (created) {
		button = created;
		// onOnline/onOffline are LaDesk's own empty hooks on the button. LaDesk
		// calls them once the widget comes back from the server, which is the
		// earliest point onClick() has a chat window to show.
		button.onOnline = flush;
		button.onOffline = flush;
	}

	function createButton () {
		var la = window.LiveAgent;

		if (!la) {
			unavailable = true;
			return;
		}

		if (typeof la.createVirtualButton === 'function') {
			useButton(la.createVirtualButton(chatId));
		} else {
			// Documented fallback: LaDesk renders its own button in place of the
			// element it is given, and removes that element. Hand it a throwaway
			// node inside a hidden wrapper so none of its markup is ever seen.
			var sink = document.createElement('div');
			sink.style.display = 'none';
			sink.appendChild(document.createElement('div'));
			document.body.appendChild(sink);
			useButton(la.createButton(chatId, sink.firstChild));
		}

		// Covers a click that arrived while the script was still loading.
		poll();
	}

	function load () {
		if (loading) {
			return;
		}
		loading = true;

		if (window.LiveAgent) {
			createButton();
			return;
		}
		if (document.getElementById(SCRIPT_ID)) {
			return;
		}

		var script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.async = true;
		script.src = SCRIPT_SRC;
		script.onload = createButton;
		script.onerror = function () {
			unavailable = true;
			openWhenReady = false;
			warn(SCRIPT_SRC + ' failed to load.');
		};
		document.head.appendChild(script);
	}

	function prewarm () {
		if (window.requestIdleCallback) {
			window.requestIdleCallback(load, { timeout: 3000 });
		} else {
			setTimeout(load, 1);
		}
	}

	// Guessing wrong here only changes when track.js is fetched, never what a
	// click does, so a coarse check is enough.
	var mayNeedPopup = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;

	if (hasRunningChat()) {
		// A conversation the visitor never closed outranks load performance: fetch
		// now so LaDesk can put it straight back on screen.
		setTimeout(load, 1);
	} else if (mayNeedPopup) {
		if (document.readyState === 'complete') {
			prewarm();
		} else {
			window.addEventListener('load', prewarm);
		}
	}

	return { open: open };
}
